import React, { useState } from 'react';
import { FaEdit, FaTrash } from 'react-icons/fa';
import { FaPlus } from 'react-icons/fa6';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';

const emptyForm = () => ({ recordId: 0, summary: '' });

// Mirrors the Summary tab on Customer Master: free-text notes added to a grid.
// The unit is implicit here (the form already belongs to one unit), so there are
// no Property/Unit pickers.
const UnitSummary = ({ summaryList, setSummaryList, readOnly = false }) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState(emptyForm());
  const [editIndex, setEditIndex] = useState(null);

  const handleAddOrUpdate = () => {
    if (!formData.summary?.trim()) {
      toast.warning(t('customerMaster.summaryFields.enterSummaryWarning'));
      return;
    }
    if (editIndex !== null) {
      setSummaryList((prev) => prev.map((item, i) => (i === editIndex ? { ...item, ...formData } : item)));
      setEditIndex(null);
    } else {
      setSummaryList((prev) => [...prev, { ...formData }]);
    }
    setFormData(emptyForm());
  };

  const handleEdit = (index) => {
    const row = summaryList[index];
    setFormData({ recordId: row.recordId ?? 0, summary: row.summary ?? '' });
    setEditIndex(index);
  };

  const handleDelete = (index) => {
    setSummaryList((prev) => prev.filter((_, i) => i !== index));
    if (editIndex === index) {
      setEditIndex(null);
      setFormData(emptyForm());
    }
  };

  return (
    <div className="cost-center-section my-3">
      <div className="border-box2 p-3" style={{ borderRadius: '8px' }}>
        {!readOnly && (
          <>
            <div className="mt-1 d-flex justify content-center w-75">
              <label className="form-label d-block mb-2" htmlFor="unit-summary-text">
                {t('customerMaster.summaryFields.summary')}
              </label>
              <textarea
                id="unit-summary-text"
                className="p-2 form-control w-100"
                rows={8}
                style={{ minHeight: '180px', resize: 'vertical' }}
                value={formData.summary}
                onChange={(e) => {
                  const value = e.target.value;
                  setFormData((prev) => ({ ...prev, summary: value }));
                  // While editing a row, keep the grid row in step with the text box so
                  // clicking Save without pressing Update Summary doesn't drop the edit.
                  if (editIndex !== null) {
                    setSummaryList((prev) => prev.map((item, i) => (i === editIndex ? { ...item, summary: value } : item)));
                  }
                }}
                placeholder={t('customerMaster.summaryFields.summary')}
              />
            </div>
            <div className="my-4">
              <div className="mainbtnSecondary flex-end gap-2 col-2" style={{ cursor: 'pointer' }} onClick={handleAddOrUpdate}>
                <FaPlus color="#fff" className="center mt-1 mx-2" />
                <div className="center">{editIndex !== null ? t('customerMaster.summaryFields.updateSummary') : t('customerMaster.summaryFields.addSummary')}</div>
              </div>
            </div>
          </>
        )}
        <div className="table-container" style={{ overflowX: 'auto', height: '400px' }}>
          <table className="table table-bordered gridshad">
            <thead className="dynoTable">
              <tr>
                {!readOnly && <th>{t('customerMaster.contactDetailsFields.action')}</th>}
                <th>{t('customerMaster.summaryFields.summary')}</th>
              </tr>
            </thead>
            <tbody>
              {summaryList.map((data, index) => (
                <tr key={index}>
                  {!readOnly && (
                    <td className="text-center d-flex gap-3">
                      <div className="center mt-2">
                        <FaEdit size={20} style={{ cursor: 'pointer', color: 'black' }} onClick={() => handleEdit(index)} />
                      </div>
                      <div className="center mt-2">
                        <FaTrash size={20} style={{ cursor: 'pointer', color: 'red' }} onClick={() => handleDelete(index)} />
                      </div>
                    </td>
                  )}
                  <td style={{ whiteSpace: 'pre-wrap', maxWidth: 720 }}>{data.summary}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default UnitSummary;

// UnitSummaryRADto body for create/update. Rows loaded from the API keep their
// own fields (createdBy/createdOn etc.); the unit identity always comes from the form.
export const buildUnitSummaryPayload = (list, { propertyNo, propertyName, unitNo, userName }) => {
  const now = new Date().toISOString();
  return (list || []).map((s) => ({
    ...s,
    recordId: s.recordId ?? 0,
    propertyNo: propertyNo || '0',
    propertyName: propertyName || '0',
    unitNo: unitNo || '0',
    summary: s.summary || '',
    link: s.link || '',
    createdBy: s.createdBy || userName || '0',
    createdOn: s.createdOn || now,
    modifiedBy: userName || '0',
    modifiedOn: now,
  }));
};
